import { useState } from "react";

import {
  Grid,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { formatDate } from "@aclymatepackages/formatters";

import Card from "../../atoms/mui/Card";

import {
  useAccountData,
  useCachedFirebaseCrud
} from "../../../helpers/firebase";
import { useQuickbooks } from "../../../helpers/integrations/quickbooks";
import {
  connectionHealth,
  hasDataGap,
  lastTransactionLabel
} from "../../../helpers/integrations/connectionHealth";
import { useSubscriptionType } from "../../../helpers/hooks/companyData";
import usePermissions from "../../../helpers/hooks/usePermissions";

const QuickbooksCard = ({ gridProps = { sm: 12 } }) => {
  const [{ status: subscriptionStatus }] = useSubscriptionType("saas");
  const { canEdit } = usePermissions();

  const [quickbooksError, setQuickbooksError] = useState("");

  const {
    pullDate,
    lastTransactionAt,
    transactionCount,
    taskStatus,
    refreshTokenExpiration,
    tokenLoading,
    launchQuickbooks,
    fetchQuickbooksData,
    currentTask,
    archived,
    id: tokenDocId
  } = useQuickbooks({ setQuickbooksError });
  const [{ integrations: integrationsArray = [] }] = useAccountData();
  const { updateAccountData, updateCollectionDoc } = useCachedFirebaseCrud();

  // No createdAt is passed: QuickBooks integration docs don't record one, and
  // pullDate is not a substitute -- it moves on every daily pull, so it would
  // hold the connection in its new-connection grace window indefinitely.
  const quickbooksIntegration = {
    name: "quickbooks",
    lastTransactionAt,
    transactionCount,
    refreshTokenExpiration
  };
  const health = connectionHealth(quickbooksIntegration);
  // Derived from the same helper rather than recomputed here, so the card and
  // the helper cannot disagree about when a token has expired.
  const isRefreshTokenExpired = health.level === "error";
  const isPulling = taskStatus === "uploading" || taskStatus === "running";
  const isPullingError = taskStatus === "error";
  const isTaskFinished = taskStatus === "finished";

  const quickbooksSubtitle = () => {
    if (isPulling) {
      if (currentTask?.status === "uploading") {
        return "Fetching your QuickBooks transactions. This may take a few minutes depending on your data size.";
      }
      if (currentTask?.status === "running") {
        const progress =
          currentTask.currentChunk && currentTask.totalChunks
            ? `Processing ${currentTask.currentChunk}/${currentTask.totalChunks} batches.`
            : "Processing your transactions.";
        return `${progress} Almost done!`;
      }
      return "Your QuickBooks data is loading. Depending on how much data you have, this could take a few minutes.";
    }
    if (!pullDate || archived) {
      return "We can connect to your QuickBooks account for faster setup";
    }
    if (isRefreshTokenExpired) {
      return "Your QuickBooks login has expired. Reconnect it to resume data syncs from your account.";
    }
    // Checked before the pullDate messages below: a daily pull can keep
    // succeeding while QuickBooks sends nothing, so pullDate alone reads as
    // healthy for a connection that has actually gone quiet.
    if (hasDataGap(quickbooksIntegration)) {
      if (health.level === "neverDelivered") {
        return `${health.label}. This connection is authorized, but QuickBooks has never sent us a transaction. Reconnect QuickBooks and confirm the account is sharing transaction data.`;
      }
      return `${health.label}. This connection is still authorized, but no new QuickBooks transactions have arrived since ${lastTransactionLabel(
        { lastTransactionAt }
      )}. Try "Fetch Latest Data", and reconnect QuickBooks if nothing arrives.`;
    }
    if (isTaskFinished && currentTask) {
      const count =
        currentTask.totalTasksProcessed || currentTask.totalTasks || 0;
      return `Last sync completed with ${count} transactions. Data from synced QuickBooks accounts is automatically pulled daily at 6 AM.`;
    }
    return `You last pulled data from QuickBooks on: ${formatDate(
      pullDate
    )}. Data from synced QuickBooks accounts is automatically pulled daily at 6 AM.`;
  };

  const onArchiveQuickbooks = async () => {
    await updateCollectionDoc("integrations", tokenDocId, { archived: true });
    return updateAccountData({
      integrations: integrationsArray.filter(
        (integration) => integration !== "quickbooks"
      )
    });
  };

  return (
    <Card
      gridProps={gridProps}
      isLoading={tokenLoading}
      cardType="layout"
      title="Quickbooks"
      subtitle={quickbooksSubtitle()}
      icon={<ReceiptIcon />}
      action={
        canEdit &&
        pullDate &&
        !archived && (
          <Tooltip title="Disconnect QuickBooks">
            <IconButton onClick={onArchiveQuickbooks}>
              <LinkOffIcon />
            </IconButton>
          </Tooltip>
        )
      }
      content={
        <Grid container direction="column" alignItems="center" spacing={2}>
          <Grid item>
            {isPulling ? (
              <CircularProgress />
            ) : !pullDate || isRefreshTokenExpired || archived ? (
              <Button
                onClick={() => launchQuickbooks()}
                disabled={subscriptionStatus !== "active" || !canEdit}
              >
                <img src="/images/quickbooksButton.png" alt="Quickbooks Icon" />
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={() => fetchQuickbooksData()}
                disabled={!canEdit}
              >
                Fetch Latest Data
              </Button>
            )}
          </Grid>
          {(quickbooksError || isPullingError) && (
            <Grid item>
              <Typography variant="body2" align="center" color="error">
                {isPullingError
                  ? "There was an error pulling your Quickbooks data. Contact support for more details."
                  : quickbooksError}
              </Typography>
            </Grid>
          )}
        </Grid>
      }
    />
  );
};

export default QuickbooksCard;
